// @flow

import { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button, Icon, Intent } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { IconNames } from '@blueprintjs/icons';
import { PanelHeader } from '../../panel';
import { ThreadDownloadActions } from '../../ai-prompt-management';
import { breakpoints } from '../../../styles/variables';
import {
  getAuthPkgUser,
  initAuthPkgUser,
} from '../../../services/auth-pkg-user';
import { authPkgConfig } from '@auravisionlabs/aura-pkg-auth-service';

// Wrapper to make top bar sticky with background extension and gap
const StickyTopBarWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--light-blue);
  padding-bottom: 0.5rem; /* Creates gap between top bar and content */
`;

// Locally compact version of PanelHeader to reduce TopBar height only
const CompactPanelHeader = styled(PanelHeader).withConfig({
  componentId: 'rendered-report-topbar-compact',
})`
  padding: 0.75rem 1rem; /* shorter header like AI top bar */
  /* Ensure rounded corners on both top and bottom to match widgets */
  border-top-left-radius: var(--widget-border-radius);
  border-top-right-radius: var(--widget-border-radius);
  border-bottom-left-radius: var(--widget-border-radius);
  border-bottom-right-radius: var(--widget-border-radius);
  margin: 0; /* No margin on header itself, gap is on wrapper */
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  flex-wrap: wrap; /* allow title/actions to stack on small screens */
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  font-family: var(--fonts-heading);
  color: var(--widget-heading-text);
`;

const TitleIcon = styled(Icon)`
  margin-right: 0.4rem;
  color: var(--widget-heading-text);
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  min-width: 0; /* allow children to shrink instead of overflowing */
  flex: 0 1 auto; /* do not stretch full width */
  margin-left: auto; /* push actions group to the far right */
  justify-content: flex-end; /* align buttons to the right inside the group */
  flex-wrap: wrap; /* allow controls to wrap on smaller screens */

  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    flex-basis: 100%; /* move actions below title on small screens */
    justify-content: flex-end; /* keep right aligned on wrap as well */
  }
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 0 1 auto;

  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    flex-basis: 100%;
  }
`;

const TopBarActions = (props) => {
  const {
    canShowEdit,
    canShowShare,
    canShowDelete,
    onEdit,
    onShare,
    onDelete,
    onPermissionsChange,
    deleteModalOpen,
    shareModalOpen,
    report,
    onNewChat,
  } = props;
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRead, setCanRead] = useState(false);

  // Run once on mount to determine permissions; default to false on error
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let authUser = getAuthPkgUser();
        if (!authUser) {
          authUser = await initAuthPkgUser();
        }
        if (!authUser) {
          if (mounted) {
            setCanUpdate(false);
            setCanDelete(false);
            setCanRead(false);
          }
          return;
        }
        const orgId = await authUser.getPrimaryOrgId();
        const { resources, authActions } = authPkgConfig;
        const [upd, del, read] = await Promise.all([
          authUser
            .can(resources.dashboardReport, authActions.update, {
              orgIds: [orgId],
            })
            .evaluate(),
          authUser
            .can(resources.dashboardReport, authActions.delete, {
              orgIds: [orgId],
            })
            .evaluate(),
          authUser
            .can(resources.dashboardReport, authActions.read, {
              orgIds: [orgId],
            })
            .evaluate(),
        ]);
        if (mounted) {
          setCanUpdate(!!upd);
          setCanDelete(!!del);
          setCanRead(!!read);
          onPermissionsChange &&
            onPermissionsChange({
              canUpdate: !!upd,
              canDelete: !!del,
              canRead: !!read,
            });
        }
      } catch (e) {
        if (mounted) {
          setCanUpdate(false);
          setCanDelete(false);
          setCanRead(false);
          onPermissionsChange &&
            onPermissionsChange({
              canUpdate: false,
              canDelete: false,
              canRead: false,
            });
        }
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAny = useMemo(
    () => !!(canShowEdit || canShowShare || canShowDelete),
    [canShowEdit, canShowShare, canShowDelete],
  );
  if (!showAny) return null;

  return (
    <StickyTopBarWrapper>
      <CompactPanelHeader>
        <HeaderRow>
          <LeftStack>
            <Title>
              <TitleIcon icon="layout" size={18} />
              Reports
            </Title>
          </LeftStack>
        <Actions>
          <ThreadDownloadActions
            headingTitle={report && (report.name || report.title)}
            headingFallback="Report"
          />
          {onNewChat && (
            <Tooltip2 content="Start a new conversation about this report">
              <Button
                intent="primary"
                icon={IconNames.REFRESH}
                text="New Chat"
                onClick={onNewChat}
              />
            </Tooltip2>
          )}
          {canShowEdit && canUpdate && (
            <Tooltip2 content="Edit Report">
              <Button intent="primary" icon="edit" text="Edit" onClick={onEdit} />
            </Tooltip2>
          )}
          {canShowShare && canUpdate && (
            <Tooltip2 content="Share Report">
              <Button
                intent="primary"
                icon="share"
                text="Share"
                onClick={onShare}
                active={shareModalOpen}
              />
            </Tooltip2>
          )}
          {canShowDelete && (
            <Tooltip2
              key={`delete-${!!deleteModalOpen}`}
              content="Delete"
              position="bottom"
              openOnTargetFocus={false}
              hoverCloseDelay={50}
            >
              <Button icon="trash" intent="danger" onClick={onDelete}>
                Delete
              </Button>
            </Tooltip2>
          )}
        </Actions>
      </HeaderRow>
      </CompactPanelHeader>
    </StickyTopBarWrapper>
  );
};

export default TopBarActions;
